import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useAdminSharedImage, useAdminDeleteSharedImage } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Trash2, LayoutTemplate, ExternalLink, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

export function SharedImageDetail() {
  const { shareId } = useParams()
  const navigate = useNavigate()
  const { data: sharedImage, isLoading, error } = useAdminSharedImage(shareId!)
  const deleteMutation = useAdminDeleteSharedImage()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [refLightbox, setRefLightbox] = useState<number | null>(null)
  const [genLightbox, setGenLightbox] = useState<number | null>(null)

  const handleDelete = async () => {
    if (!shareId) return

    try {
      await deleteMutation.mutateAsync(shareId)
      toast.success('공개 이미지가 삭제되었습니다')
      navigate('/admin/shared-images')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '삭제에 실패했습니다')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!sharedImage) return null

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/shared-images">공개된 이미지</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">공개 이미지 상세</h1>
          <p className="text-caption mt-2 leading-relaxed">
            커뮤니티에 공유된 이미지의 상세 정보입니다
          </p>
        </div>
        <Button
          variant="destructive"
          onClick={() => setShowDeleteDialog(true)}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          공개 삭제
        </Button>
      </div>

      {/* Image Preview */}
      <div className="bg-white rounded-lg border p-6">
        <label className="text-sm font-medium text-gray-700 block mb-4">공개 이미지</label>
        <div className="flex justify-center bg-gray-50 rounded-lg p-4">
          <img
            src={sharedImage.imageUrl}
            alt="공개 이미지"
            className="max-w-full max-h-[500px] rounded-lg shadow-lg object-contain"
          />
        </div>
      </div>

      {/* Share Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <h2 className="text-lg font-semibold text-heading tracking-tight">공유 정보</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">공유 ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{sharedImage.id}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">공개자</label>
            <div className="mt-1 text-gray-900">{sharedImage.userEmail ?? '-'}</div>
            <div className="text-xs text-gray-400 mt-1 font-mono">{sharedImage.userId}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">비율</label>
            <div className="mt-1">
              <Badge variant="outline">{sharedImage.aspectRatio}</Badge>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">공개일시</label>
            <div className="mt-1 text-gray-900 text-sm">
              {new Date(sharedImage.createdAt).toLocaleString('ko-KR')}
            </div>
          </div>

          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">공유 URL</label>
            {(() => {
              const basePath = (import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '')
              const shareUrl = `${window.location.origin}${basePath}/share/${sharedImage.id}`
              return (
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 p-2 bg-gray-50 rounded text-sm font-mono text-gray-700">
                    {shareUrl}
                  </code>
                  <a
                    href={`${basePath}/share/${sharedImage.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              )
            })()}
          </div>
        </div>
      </div>

      {/* Generation Info */}
      {sharedImage.generation && (
        <div className="bg-white rounded-lg border p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-heading tracking-tight">생성 정보</h2>
            <Link
              to={`/admin/generations/${sharedImage.generationId}/view`}
              className="text-sm text-primary hover:underline"
            >
              생성 상세 보기
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700">스타일</label>
              <div className="mt-1 text-gray-900">{sharedImage.generation.style || '-'}</div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">모델</label>
              <div className="mt-1 text-gray-900 text-sm">
                {sharedImage.generation.modelId || '-'}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">생성일시</label>
              <div className="mt-1 text-gray-900 text-sm">
                {new Date(sharedImage.generation.createdAt).toLocaleString('ko-KR')}
              </div>
            </div>

            {sharedImage.generation.generatedImages && (
              <div>
                <label className="text-sm font-medium text-gray-700">생성된 이미지 수</label>
                <div className="mt-1 text-gray-900">
                  {sharedImage.generation.generatedImages.length}개
                </div>
              </div>
            )}

            {/* Template */}
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-700">템플릿</label>
              {sharedImage.template ? (
                <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  {sharedImage.template.thumbnailUrl ? (
                    <img
                      src={sharedImage.template.thumbnailUrl}
                      alt={sharedImage.template.name}
                      className="w-16 h-16 rounded-lg object-cover border"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                      <LayoutTemplate className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <Link
                      to={`/admin/templates/${sharedImage.template.id}`}
                      className="font-medium text-gray-900 hover:text-primary transition-colors"
                    >
                      {sharedImage.template.name}
                    </Link>
                    {sharedImage.template.description && (
                      <p className="text-sm text-gray-500 mt-0.5">
                        {sharedImage.template.description}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-1 text-gray-400">사용된 템플릿 없음</div>
              )}
            </div>
          </div>

          {/* Prompt */}
          <div>
            <label className="text-sm font-medium text-gray-700">프롬프트</label>
            <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
              {sharedImage.generation.prompt || '-'}
            </div>
          </div>

          {/* Reference Images */}
          {sharedImage.generation.referenceImageUrls &&
            sharedImage.generation.referenceImageUrls.length > 0 &&
            (() => {
              const refUrls = sharedImage.generation.referenceImageUrls!
              return (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-4">
                    참조 이미지 ({refUrls.length}개)
                  </label>
                  <div className="grid grid-cols-4 gap-4">
                    {refUrls.map((url, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-100 rounded-lg overflow-hidden cursor-pointer ring-offset-2 hover:ring-2 hover:ring-primary/40 transition-shadow"
                        onClick={() => setRefLightbox(idx)}
                      >
                        <img
                          src={url}
                          alt={`참조 이미지 ${idx + 1}`}
                          className="w-full h-auto object-contain"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>

                  <Dialog
                    open={refLightbox !== null}
                    onOpenChange={(open) => {
                      if (!open) setRefLightbox(null)
                    }}
                  >
                    <DialogContent className="max-w-3xl p-0 border-0 bg-transparent shadow-none [&>button]:hidden">
                      <DialogTitle className="sr-only">참조 이미지 미리보기</DialogTitle>
                      {refLightbox !== null && (
                        <div className="relative flex items-center justify-center">
                          <img
                            src={refUrls[refLightbox]}
                            alt={`참조 이미지 ${refLightbox + 1}`}
                            className="max-h-[80vh] w-auto rounded-lg object-contain"
                          />
                          <button
                            onClick={() => setRefLightbox(null)}
                            className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                          {refUrls.length > 1 && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setRefLightbox(
                                    (refLightbox - 1 + refUrls.length) % refUrls.length,
                                  )
                                }}
                                className="absolute left-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                              >
                                <ChevronLeft className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setRefLightbox((refLightbox + 1) % refUrls.length)
                                }}
                                className="absolute right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                              >
                                <ChevronRight className="w-5 h-5" />
                              </button>
                              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                                {refLightbox + 1} / {refUrls.length}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
              )
            })()}

          {/* Other Generated Images */}
          {sharedImage.generation.generatedImages &&
            sharedImage.generation.generatedImages.length > 1 &&
            (() => {
              const otherImages = sharedImage.generation.generatedImages!.filter(
                (img) => img.s3Key !== sharedImage.s3Key,
              )
              if (otherImages.length === 0) return null
              return (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-4">
                    동일 생성의 다른 이미지
                  </label>
                  <div className="grid grid-cols-4 gap-4">
                    {otherImages.map((img, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-100 rounded-lg overflow-hidden border cursor-pointer ring-offset-2 hover:ring-2 hover:ring-primary/40 transition-shadow"
                        onClick={() => setGenLightbox(idx)}
                      >
                        <img
                          src={img.imageUrl || `/api/images/${img.s3Key}`}
                          alt={`생성 이미지 ${idx + 1}`}
                          className="w-full h-auto object-contain"
                          loading="lazy"
                        />
                        <div className="text-xs text-gray-500 text-center py-1">
                          seed: {img.seed}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Dialog
                    open={genLightbox !== null}
                    onOpenChange={(open) => {
                      if (!open) setGenLightbox(null)
                    }}
                  >
                    <DialogContent className="max-w-3xl p-0 border-0 bg-transparent shadow-none [&>button]:hidden">
                      <DialogTitle className="sr-only">생성 이미지 미리보기</DialogTitle>
                      {genLightbox !== null && (
                        <div className="relative flex items-center justify-center">
                          <img
                            src={
                              otherImages[genLightbox].imageUrl ||
                              `/api/images/${otherImages[genLightbox].s3Key}`
                            }
                            alt={`생성 이미지 ${genLightbox + 1}`}
                            className="max-h-[80vh] w-auto rounded-lg object-contain"
                          />
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                            seed: {otherImages[genLightbox].seed}
                          </div>
                          <button
                            onClick={() => setGenLightbox(null)}
                            className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                          {otherImages.length > 1 && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setGenLightbox(
                                    (genLightbox - 1 + otherImages.length) % otherImages.length,
                                  )
                                }}
                                className="absolute left-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                              >
                                <ChevronLeft className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setGenLightbox((genLightbox + 1) % otherImages.length)
                                }}
                                className="absolute right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                              >
                                <ChevronRight className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
              )
            })()}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>공개 이미지 삭제</DialogTitle>
            <DialogDescription className="mt-2">
              이 이미지를 커뮤니티에서 삭제하시겠습니까?
              <br />
              원본 이미지는 생성자의 히스토리에 유지됩니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteMutation.isPending}
            >
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '삭제 중...' : '삭제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
